import {
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import type {
  Job,
  JobFilters,
} from '../types/job';

import {
  fetchJobs,
  fetchDashboardJobs,
  fetchJobById,
  createJob,
  updateJob,
  updateJobStatus,
  deleteJob,
} from './jobThunk';

interface JobState {
  jobs: Job[];
  selectedJob: Job | null;

  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };

  filters: JobFilters;

  loading: boolean;

  error: string | null;
}

const initialState: JobState = {
  jobs: [],

  selectedJob: null,

  pagination: {
    total: 0,
    page: 1,
    limit: 0,
    totalPages: 0,
  },

  filters: {
    search: '',
    department: '',
    workplaceType: '',
    employmentType: '',
    status: '',
    page: 1,
  },

  loading: false,

  error: null,
};

const jobSlice = createSlice({
  name: 'jobs',

  initialState,

  reducers: {
    clearSelectedJob: (state) => {
      state.selectedJob = null;
    },

    clearJobError: (state) => {
      state.error = null;
    },

    setFilters: (
      state,
      action: PayloadAction<
        Partial<JobFilters>
      >
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    resetFilters: (state) => {
      state.filters = {
        ...initialState.filters,
      };
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(
        fetchJobs.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchJobs.fulfilled,
        (state, action) => {
          state.loading = false;

          state.jobs =
            action.payload.jobs;

          state.pagination =
            action.payload.pagination;
        }
      )

      .addCase(
        fetchJobs.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to fetch jobs';
        }
      )

      .addCase(
        fetchDashboardJobs.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchDashboardJobs.fulfilled,
        (state, action) => {
          state.loading = false;

          state.jobs =
            action.payload.jobs;

          state.pagination =
            action.payload.pagination;
        }
      )

      .addCase(
        fetchDashboardJobs.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to fetch dashboard jobs';
        }
      )

      .addCase(
        fetchJobById.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        fetchJobById.fulfilled,
        (state, action) => {
          state.loading = false;

          state.selectedJob =
            action.payload;
        }
      )

      .addCase(
        fetchJobById.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to fetch job';
        }
      )

      .addCase(
        createJob.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        createJob.fulfilled,
        (state, action) => {
          state.loading = false;

          if ('jobCode' in action.payload) {
            state.jobs.unshift(action.payload);
          }
        }
      )

      .addCase(
        createJob.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to create job';
        }
      )

      .addCase(
        updateJob.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )


      .addCase(
        updateJob.fulfilled,
        (state, action) => {
          state.loading = false;

          const result = action.payload;

          if ('jobCode' in result) {
            const updatedJob: Job = result;

            state.jobs = state.jobs.map((job) =>
              job.id === updatedJob.id
                ? updatedJob
                : job
            );

            state.selectedJob = updatedJob;
          }
        }
      )


      .addCase(
        updateJob.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to update job';
        }
      )

      .addCase(
        updateJobStatus.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        updateJobStatus.fulfilled,
        (state, action) => {
          state.loading = false;

          state.jobs =
            state.jobs.map(
              (job) =>
                job.id ===
                  action.payload.id
                  ? action.payload
                  : job
            );

          state.selectedJob =
            action.payload;
        }
      )

      .addCase(
        updateJobStatus.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to update job status';
        }
      )

      .addCase(
        deleteJob.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(
        deleteJob.fulfilled,
        (state, action) => {
          state.loading = false;

          state.jobs =
            state.jobs.filter(
              (job) =>
                job.id !== action.payload
            );
        }
      )

      .addCase(
        deleteJob.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ??
            'Failed to delete job';
        }
      );
  },
});

export const {
  clearSelectedJob,
  clearJobError,
  setFilters,
  resetFilters,
} = jobSlice.actions;

export default jobSlice.reducer;